import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Monitor } from 'lucide-react';
import {
  canInstall,
  showInstallPrompt,
  isAppInstalled,
  isPWAMode,
} from '../../services/pwa';

interface InstallBannerProps {
  className?: string;
  variant?: 'banner' | 'modal' | 'toast';
  autoShow?: boolean;
  onInstall?: () => void;
  onDismiss?: () => void;
}

export const PWAInstallBanner: React.FC<InstallBannerProps> = ({
  className = '',
  variant = 'banner',
  autoShow = true,
  onInstall,
  onDismiss,
}) => {
  const [show, setShow] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!autoShow) return;

    const checkInstallability = () => {
      const canInstallApp = canInstall();
      const isAlreadyInstalled = isAppInstalled();
      const isInPWAMode = isPWAMode();
      const wasDismissed =
        localStorage.getItem('yapzap-install-dismissed') === 'true';

      setShow(
        canInstallApp && !isAlreadyInstalled && !isInPWAMode && !wasDismissed
      );
    };

    // Check immediately
    checkInstallability();

    // Listen for install availability
    const handleInstallAvailable = () => {
      checkInstallability();
    };

    const handleInstalled = () => {
      setShow(false);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-installed', handleInstalled);

    return () => {
      window.removeEventListener(
        'pwa-install-available',
        handleInstallAvailable
      );
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, [autoShow]);

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      const success = await showInstallPrompt();
      if (success) {
        onInstall?.();
        setShow(false);
      }
    } catch (error) {
      console.error('Install failed:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShow(false);
    localStorage.setItem('yapzap-install-dismissed', 'true');
    onDismiss?.();
  };

  if (!show || dismissed) {
    return null;
  }

  const bannerContent = (
    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow-lg">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <Download className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Install YapZap</h3>
          <p className="text-xs text-blue-100">
            Get the full app experience with offline access and notifications
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={handleInstall}
          disabled={isInstalling}
          className="px-3 py-1.5 bg-white text-blue-600 text-xs font-medium rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isInstalling ? 'Installing...' : 'Install'}
        </button>
        <button
          onClick={handleDismiss}
          className="p-1 text-blue-200 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center mb-4">
            <Download className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Install YapZap
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            Install YapZap on your device for the best experience with offline
            access, notifications, and faster loading.
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex items-center space-x-3 text-sm text-gray-700">
              <Smartphone className="w-4 h-4 text-blue-600" />
              <span>Access from your home screen</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-700">
              <Monitor className="w-4 h-4 text-blue-600" />
              <span>Works offline</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-700">
              <Download className="w-4 h-4 text-blue-600" />
              <span>Faster loading times</span>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleDismiss}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={handleInstall}
              disabled={isInstalling}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isInstalling ? 'Installing...' : 'Install Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const toastContent = (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm z-50">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <Download className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">Install YapZap</p>
          <p className="text-xs text-gray-500 mt-1">
            Get the full app experience
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleInstall}
            disabled={isInstalling}
            className="px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
          >
            {isInstalling ? '...' : 'Install'}
          </button>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  switch (variant) {
    case 'modal':
      return modalContent;
    case 'toast':
      return toastContent;
    default:
      return <div className={`w-full ${className}`}>{bannerContent}</div>;
  }
};

export default PWAInstallBanner;
